import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Alert,
  Box,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  useTheme,
  Checkbox,
  FormControlLabel,
  Chip,
  Snackbar,
} from "@mui/material";
import {
  CheckCircle,
  AccessTime,
  Visibility,
  Search,
  Add,
} from "@mui/icons-material";
import EditableGrid from "../../components/editableGrid";
import { GridActionsCellItem, GridColDef, GridRowId } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { Coachee, Session, SessionStatus } from "../../models";
import { SessionService } from "../../services/coachingNavigator/sessionService";
import { CoacheeService } from "../../services/coachingNavigator/coacheeService";
import config from "../../config";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { TooltipIcon } from "../../components/tooltipIcon";
import { useNavigate } from "react-router-dom";
import CreateSessionPanel from "../../components/coachingNavigator/createSessionPanel";
import ConfirmationPanel from "../../components/confirmationPanel";
import {
  CoachingClient,
  LinkClientDialog,
} from "../../components/coachingNavigator/coachingCalendar";
import {
  LIBRARY_PAGE_HEADER_HEIGHT,
  LibraryPageHeader,
} from "../../components/libraryPageHeader";
import { Context } from "../../components/context";

const sessionService = new SessionService(config.api.baseUrl, "/sessions");
const coacheeService = new CoacheeService(config.api.baseUrl, "/coachee");

const CoachingSessions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const appContext = React.useContext(Context);
  const creatorEmail = useMemo(
    () => (appContext.state.invoker?.email || "").trim(),
    [appContext.state.invoker?.email],
  );
  const [sessions, setSessions] = useState<Session[]>([]);
  const [searchText, setSearchText] = useState("");
  const [coacheesById, setCoacheesById] = useState<Record<string, Coachee>>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Session | null>(null);
  const [deleteError, setDeleteError] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<Session | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [unlinkError, setUnlinkError] = useState(false);
  const [linkTarget, setLinkTarget] = useState<Session | null>(null);
  const [linkError, setLinkError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const clients: CoachingClient[] = useMemo(
    () =>
      Object.values(coacheesById)
        .filter((c) => c && (c as Coachee).active !== false && c.id)
        .map((c) => ({
          id: (c.id || "").toString(),
          name: c.name,
          company: (c as Coachee).company || "-",
        })),
    [coacheesById],
  );

  const sortSessions = useCallback((items: Session[]) => {
    const toMillis = (s: Session) => {
      const when = s.startTime as unknown as string | Date | undefined;
      return when ? new Date(when).getTime() : Number.MAX_SAFE_INTEGER;
    };
    return [...items].sort((a, b) => toMillis(a) - toMillis(b));
  }, []);

  const { i18n: i18nInstance } = useTranslation();
  const formatDate = useCallback(
    (value?: Date | string) => {
      if (!value) return "";
      const date = new Date(value);
      return date.toLocaleString(i18nInstance.language || undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    [i18nInstance.language],
  );

  const onClientDetails = useCallback(
    (clientId?: string) => {
      if (!clientId) return;
      navigate(`/coaching-hub/${clientId}`);
    },
    [navigate],
  );

  const onSessionOverview = useCallback(
    (session?: Session) => {
      const clientId = session?.clientId;
      if (clientId) {
        navigate(`/sessionOverview/${clientId}`);
        return;
      }
      navigate("/sessions");
    },
    [navigate],
  );

  const onOpenSession = useCallback(
    (id: GridRowId) => {
      if (!id) return;
      navigate(`/sessions/${id}`, {
        state: { returnTo: "/sessions" },
      });
    },
    [navigate],
  );

  const handleConfirmUnlink = useCallback(async () => {
    if (!unlinkTarget?.id || isUnlinking) return;
    const id = unlinkTarget.id;
    setIsUnlinking(true);
    try {
      // Schema types clientId as ObjectId; PATCH with null clears it. Empty string fails Mongoose cast.
      const updated = await sessionService.patch(id, {
        clientId: null as unknown as string,
      });
      setSessions((prev) =>
        prev.map((s) =>
          s.id === id ? { ...s, ...updated, clientId: updated?.clientId ?? "" } : s,
        ),
      );
      setUnlinkTarget(null);
    } catch (err) {
      console.error("Failed to unlink client from session", err);
      setUnlinkError(true);
      setUnlinkTarget(null);
    } finally {
      setIsUnlinking(false);
    }
  }, [unlinkTarget, isUnlinking]);

  const handleLinkSelected = useCallback(
    async (clientId: string) => {
      if (!linkTarget?.id || !clientId) return;
      const id = linkTarget.id;
      try {
        const updated = await sessionService.patch(id, { clientId });
        setSessions((prev) =>
          prev.map((s) =>
            s.id === id ? { ...s, ...updated, clientId: updated?.clientId ?? clientId } : s,
          ),
        );
      } catch (err) {
        console.error("Failed to link client to session", err);
        setLinkError(true);
        // Re-throw so LinkClientDialog can surface failure in the progress dialog.
        throw err;
      } finally {
        setLinkTarget(null);
      }
    },
    [linkTarget],
  );

  const handleDeleteSession = useCallback(async () => {
    if (!deleteTarget?.id || isDeleting) return;
    setIsDeleting(true);
    const id = deleteTarget.id;
    try {
      await sessionService.delete(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Failed to delete session", err);
      setDeleteError(true);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, isDeleting]);

  const StatusCell = useMemo(
    () =>
      ({
        status,
        t,
      }: {
        status: SessionStatus;
        t: (key: string) => string;
      }) => {
        const isDraft = status === SessionStatus.Draft;
        const isPlanned = status === SessionStatus.Planned;
        const isPrepared = status === SessionStatus.Prepared;
        const isCompleted = status === SessionStatus.Completed;

        return (
          <Chip
            sx={{ minWidth: "60px", justifyContent: "left", width: "100%" }}
            icon={
              isCompleted ? (
                <CheckCircle />
              ) : isPlanned ? (
                <AccessTime />
              ) : isPrepared ? (
                <Visibility />
              ) : undefined
            }
            label={
              isDraft
                ? t("coachingNavigator.sessionStatusChip.draft")
                : isPlanned
                  ? t("coachingNavigator.sessionStatusChip.planned")
                  : isPrepared
                    ? t("coachingNavigator.sessionStatusChip.prepared")
                    : isCompleted
                      ? t("coachingNavigator.sessionStatusChip.completed")
                      : "default"
            }
            color={
              isCompleted
                ? "success"
                : isPrepared
                  ? "info"
                  : isPlanned
                    ? "info" // using same color as prepared for now, as we don't have a separate color for planned (grey doesn't work well for visibility)
                    : isDraft
                      ? "warning"
                      : "default"
            }
            size="small"
          />
        );
      },
    [],
  );

  const columns = useMemo(
    (): GridColDef[] => [
      {
        field: "name",
        headerName: t("coachingNavigator.name") || "Name",
        flex: 0.3,
        minWidth: 160,
      },
      {
        field: "clientId",
        headerName: t("coachingNavigator.client") || "Client",
        flex: 0.25,
        minWidth: 160,
        maxWidth: 200,
        renderCell: ({ value }) => {
          const name = coacheesById?.[value as string]?.name;
          if (name) return name;
          return (
            <Box
              component="span"
              sx={{ color: "#C4C9D4", fontStyle: "italic", fontSize: "0.8rem" }}
            >
              {t("coachingNavigator.noClientLinked", {
                defaultValue: "No client linked",
              })}
            </Box>
          );
        },
      },
      {
        field: "startTime",
        headerName: t("coachingNavigator.sessionStart") || "Start",
        flex: 0.2,
        minWidth: 180,
        maxWidth: 180,
        renderCell: ({ value }) => formatDate(value as string),
      },
      {
        field: "status",
        headerName: t("coachingNavigator.sessionStatus") || "Status",
        flex: 0.1,
        minWidth: 150,
        maxWidth: 150,
        renderCell: (params) => {
          return <StatusCell status={params.row.status} t={t} />;
        },
      },
      {
        field: "actions",
        headerName: "",
        type: "actions",
        getActions: (params) => {
          const row = params.row as Session;
          const hasClientLink = !!row.clientId;
          const hasClientData = !!(
            row.clientId && coacheesById?.[row.clientId]
          );
          return [
            <GridActionsCellItem
              key="deleteSession"
              icon={
                <TooltipIcon
                  titleKey={"general.delete"}
                  icon={<DeleteOutlineOutlinedIcon />}
                />
              }
              label="Delete Session"
              onClick={() => setDeleteTarget(row)}
              style={{ color: "var(--mui-palette-error-main)" }}
            />,
            <GridActionsCellItem
              key="clientDetails"
              icon={
                <TooltipIcon
                  titleKey={
                    hasClientData
                      ? "coachingNavigator.clientDetails"
                      : "coachingNavigator.noClientData"
                  }
                  icon={<PersonOutlineOutlinedIcon />}
                />
              }
              label="Client Details"
              disabled={!hasClientData}
              onClick={() => onClientDetails(row.clientId)}
              style={{
                color: hasClientData
                  ? "var(--mui-palette-primary-main)"
                  : "var(--mui-palette-action-disabled)",
                pointerEvents: "auto",
                cursor: hasClientData ? "pointer" : "not-allowed",
              }}
            />,
            <GridActionsCellItem
              key="sessionOverview"
              icon={
                <TooltipIcon
                  titleKey={
                    hasClientData
                      ? "coachingNavigator.sessionOverview"
                      : "coachingNavigator.noClientData"
                  }
                  icon={<EventNoteOutlinedIcon />}
                />
              }
              label="Session Overview"
              disabled={!hasClientData}
              onClick={() => onSessionOverview(row)}
              style={{
                color: hasClientData
                  ? "var(--mui-palette-primary-main)"
                  : "var(--mui-palette-action-disabled)",
                pointerEvents: "auto",
                cursor: hasClientData ? "pointer" : "not-allowed",
              }}
            />,
            hasClientLink ? (
              <GridActionsCellItem
                key="unlinkClient"
                icon={
                  <TooltipIcon
                    titleKey="coachingNavigator.unlinkClient"
                    icon={<LinkOffOutlinedIcon />}
                  />
                }
                label={t("coachingNavigator.unlinkClient")}
                onClick={() => setUnlinkTarget(row)}
                style={{
                  color: "var(--mui-palette-warning-main)",
                  pointerEvents: "auto",
                  cursor: "pointer",
                }}
              />
            ) : (
              <GridActionsCellItem
                key="linkClient"
                icon={
                  <TooltipIcon
                    titleKey="coachingNavigator.linkClient"
                    icon={<LinkOutlinedIcon />}
                  />
                }
                label={t("coachingNavigator.linkClient", {
                  defaultValue: "Link client to session",
                })}
                onClick={() => setLinkTarget(row)}
                style={{
                  color: "var(--mui-palette-primary-main)",
                  pointerEvents: "auto",
                  cursor: "pointer",
                }}
              />
            ),
            <GridActionsCellItem
              key="openSession"
              icon={
                <TooltipIcon
                  titleKey={"coachingNavigator.openSession"}
                  icon={<KeyboardArrowRightOutlinedIcon />}
                />
              }
              label="Open Session"
              onClick={() => onOpenSession(params.id)}
              style={{ color: "var(--mui-palette-primary-main)" }}
            />,
          ];
        },
        width: 220,
        minWidth: 220,
        sortable: false,
        filterable: false,
      },
    ],
    [
      formatDate,
      t,
      coacheesById,
      onClientDetails,
      onOpenSession,
      onSessionOverview,
    ],
  );

  const loadSessions = useCallback(async () => {
    try {
      if (!creatorEmail) {
        setSessions([]);
        return;
      }

      try {
        await sessionService.refreshByCreator(creatorEmail);
      } catch (err) {
        console.error("Failed to refresh sessions for creator", err);
      }

      const data = await sessionService.getByCreator(creatorEmail);

      // Auto-demote prepared sessions that ended to draft (client-side guard)
      const now = Date.now();
      const toUpdate = (data || []).filter((s) => {
        const status = (s.status || "").toString().toLowerCase();
        if (status !== SessionStatus.Prepared) return false;
        if (!s.endTime) return false;
        const end = new Date(s.endTime).getTime();
        if (Number.isNaN(end)) return false;
        return end < now;
      });

      if (toUpdate.length) {
        // Fire-and-forget updates; don't block UI
        toUpdate.forEach((s) => {
          sessionService
            .save({ ...s, status: SessionStatus.Draft })
            .catch((err) => {
              console.error("Failed to auto-update session status", err);
            });
        });
      }

      const updatedList = (data || []).map((s) => {
        const shouldDraft = toUpdate.find((u) => u.id === s.id);
        return shouldDraft ? { ...s, status: SessionStatus.Draft } : s;
      });

      const sorted = sortSessions(updatedList);
      setSessions(sorted);
    } catch (err) {
      console.error("Failed to load sessions", err);
    } finally {
      setIsLoading(false);
    }
  }, [creatorEmail, sortSessions]);

  const loadCoachees = useCallback(async () => {
    try {
      if (!creatorEmail) {
        setCoacheesById({});
        return;
      }

      const data = await coacheeService.getByCreator(creatorEmail);
      const map: Record<string, Coachee> = {};
      (data || []).forEach((c) => {
        if (c.id) {
          map[c.id.toString()] = c;
        }
      });
      setCoacheesById(map);
    } catch (err) {
      console.error("Failed to load coachees", err);
    }
  }, [creatorEmail]);

  const handleCreated = useCallback(
    (session: Session) => {
      setSessions((prev) => sortSessions([...(prev || []), session]));
      setCreateOpen(false);
      loadCoachees();
    },
    [sortSessions, loadCoachees],
  );

  useEffect(() => {
    loadSessions();
    loadCoachees();
  }, [loadSessions, loadCoachees]);

  const gridItems = useMemo<Session[]>(() => {
    const term = searchText.trim().toLowerCase();
    const base =
      (showCompleted
        ? sessions
        : sessions.filter((s) => s.status !== SessionStatus.Completed)) || [];
    if (!term) return sortSessions(base);
    const filtered = base.filter((s) => {
      const clientName = s.clientId
        ? coacheesById[s.clientId]?.name?.toLowerCase()
        : "";
      return (
        s.name?.toLowerCase().includes(term) ||
        clientName?.includes(term) ||
        s.clientId?.toLowerCase().includes(term) ||
        s.status?.toLowerCase().includes(term)
      );
    });
    return sortSessions(filtered);
  }, [sessions, searchText, coacheesById, sortSessions, showCompleted]);

  return (
    <Box
      sx={{ pt: 2, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <LibraryPageHeader
        title={t("coachingNavigator.upcomingSessions") || "Sessions"}
        chipLabel={t("coachingNavigator.sessionCount", {
          count: gridItems.length,
        })}
        rightContent={
          <>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e) => setShowCompleted(e.target.checked)}
                  checked={showCompleted}
                  sx={{
                    border: "none",
                    "&.MuiToggleButton-root": {
                      color: theme.palette.primary.main,
                    },
                    "&.Mui-selected": {
                      color: theme.palette.primary.main,
                      backgroundColor: theme.palette.action.selected,
                      "&:hover": {
                        backgroundColor: theme.palette.action.focus,
                      },
                    },
                    "&:hover": {
                      color: theme.palette.primary.main,
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                />
              }
              label={
                t("coachingNavigator.showCompletedSessions") ||
                "Show completed sessions"
              }
            />
            <TextField
              size="small"
              placeholder={
                t("coachingNavigator.searchPlaceholderSessions") || "Search"
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: 260,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        sx={{ color: "text.secondary", fontSize: "1.25rem" }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              {t("coachingNavigator.createSession") || "Create Session"}
            </Button>
          </>
        }
      />

      <Box sx={{ flexGrow: 1 }}>
        <EditableGrid
          columns={columns}
          rows={gridItems}
          actionsHeaderKey=""
          height={`calc(90vh - ${LIBRARY_PAGE_HEADER_HEIGHT}px)`}
          toolbar={() => null}
          loading={isLoading}
        />
      </Box>

      <CreateSessionPanel
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <ConfirmationPanel
        showModal={!!deleteTarget}
        hideModal={() => setDeleteTarget(null)}
        confirmModal={handleDeleteSession}
        action="delete"
        title={t("coachingNavigator.sessionDialog.delete.title")}
        content={t("coachingNavigator.sessionDialog.delete.body")}
      />

      <ConfirmationPanel
        action="delete"
        showModal={!!unlinkTarget}
        hideModal={() => {
          if (!isUnlinking) setUnlinkTarget(null);
        }}
        confirmModal={() => void handleConfirmUnlink()}
        title={t("coachingNavigator.sessionDialog.unlink.title", {
          defaultValue: "Unlink Client?",
        })}
        content={t("coachingNavigator.sessionDialog.unlink.body", {
          clientName: unlinkTarget?.clientId
            ? (coacheesById[unlinkTarget.clientId]?.name ?? "")
            : "",
          defaultValue:
            "Are you sure you want to unlink {{clientName}} from this session? You can re-link a client at any time.",
        })}
        customActions={
          <>
            <Button
              variant="outlined"
              onClick={() => setUnlinkTarget(null)}
              disabled={isUnlinking}
            >
              {t("button.cancel")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={isUnlinking ? null : <DeleteIcon />}
              onClick={() => void handleConfirmUnlink()}
              disabled={isUnlinking}
              autoFocus
            >
              {isUnlinking ? (
                <CircularProgress size={16} color="error" />
              ) : (
                t("coachingNavigator.sessionDialog.unlink.confirm", {
                  defaultValue: "Unlink",
                })
              )}
            </Button>
          </>
        }
      />

      <LinkClientDialog
        open={!!linkTarget}
        clients={clients}
        selectedCount={1}
        existingLinkages={{}}
        onClose={() => setLinkTarget(null)}
        onLink={handleLinkSelected}
      />

      <Snackbar
        open={deleteError}
        autoHideDuration={4000}
        onClose={() => setDeleteError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setDeleteError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {t("coachingNavigator.sessionDialog.delete.error")}
        </Alert>
      </Snackbar>

      <Snackbar
        open={unlinkError}
        autoHideDuration={4000}
        onClose={() => setUnlinkError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setUnlinkError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {t("coachingNavigator.sessionDialog.unlink.error", {
            defaultValue: "Failed to unlink client. Please try again.",
          })}
        </Alert>
      </Snackbar>

      <Snackbar
        open={linkError}
        autoHideDuration={4000}
        onClose={() => setLinkError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setLinkError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {t("coachingNavigator.sessionDialog.link.error", {
            defaultValue: "Failed to link client. Please try again.",
          })}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CoachingSessions;
